import { Component, computed, inject, input } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { ProductItem } from '../../model/product-item';
import { ProductPrice } from '../../model/product-price';
import { BuyService } from '../../service/buy.service';
import { MoneyPipe } from '../../money.pipe';

const ON_SALE = ['NEW', 'AVAILABLE', 'FEW_LEFT'];

/** The `product-item` partial: one card in a grid, a carousel or a recommendation strip. */
@Component({
  selector: 'product-item',
  imports: [RouterLink, MoneyPipe],
  host: {
    style: 'display: contents'
  },
  template: `
    <!-- Block2 -->
    <div class="block2 product-item">
      <div [class]="imageClass()">
        <a [routerLink]="product().uri">
          @if (product().photos.length) {
            <img class="lazyload" [attr.data-src]="product().photos[0]" [alt]="product().name">
          }
        </a>
      </div>

      <div class="row">
        <div class="block2-txt p-t-20">
          <div class="column col-sm">
            <a class="block2-name dis-block s-text3 p-b-5" [routerLink]="product().uri">
              {{product().name}}
            </a>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="column col-sm p-t-10 d-flex align-items-center">
          <div class="block2-txt">
            @if (oldPrice()) {
              <span class="block2-oldprice m-text7 p-r-5">
                {{oldPrice() | money}}
              </span>
              <br>
              <span class="block2-newprice m-text8 p-r-5 fglight">
                {{product().price | money}}
              </span>
            } @else {
              <span class="block2-price m-text6 p-r-5">
                {{product().price | money}}
              </span>
            }
          </div>
        </div>
        <div class="column col-sm p-t-10 d-flex flex-column align-items-stretch justify-content-end">
          <div class="block2-txt">
            @if (product().collection) {
              <button type="button" class="flex-c-m size1 bg4 bo-rad-23 hov1 s-text1 trans-0-4"
                [id]="product().id" [name]="product().name" (click)="choose()">
                Вибрати
              </button>
            } @else if (buyable()) {
              <button type="button" class="flex-c-m size1 bg4 bo-rad-23 hov1 s-text1 trans-0-4 buy"
                [id]="product().id" [name]="product().name" [attr.price]="product().price?.value"
                (click)="buy()">
                Купити
              </button>
            } @else {
              <span class="flex-c-m size1 s-text8">
                {{product().status === 'DRAFT' ? 'Чернетка' : 'Товар закінчився'}}
              </span>
            }
          </div>
        </div>
      </div>
    </div>
    `
})
export class ProductItemComponent {

  readonly product = input.required<ProductItem>();

  private buyService = inject(BuyService);
  private router = inject(Router);

  /** What it cost before the discount, which is the discount added back on. */
  readonly oldPrice = computed<ProductPrice | undefined>(() => {
    const price = this.product().price;
    if (price?.value === undefined || !price.discount?.value) {
      return undefined;
    }
    return { ...price, value: price.value + price.discount.value, discount: undefined };
  });

  readonly buyable = computed(() => ON_SALE.includes(this.product().status ?? ''));

  /** The theme paints the sale / new / sold-out ribbon with a class on the image wrapper. */
  readonly imageClass = computed(() =>
    'block2-img wrap-pic-w of-hidden pos-relative hov-img-zoom ' + this.labelClass());

  readonly labelClass = computed(() => {
    const product = this.product();
    if (product.price?.discount) {
      return 'block2-labelsale';
    }
    switch (product.status) {
      case 'NEW':
        return 'block2-labelnew';
      case 'DRAFT':
        return 'block2-labelsale draft';
      case 'SOLD_OUT':
        return 'block2-labelsale soldout';
      default:
        return '';
    }
  });

  choose() {
    return this.router.navigateByUrl(this.product().uri);
  }

  buy() {
    return this.buyService.buy(this.product().id, this.product().name);
  }

}
